// Staff tool — self-contained API + session helpers (§6.1 / §8).
//
// IDENTITY (A4): the staff session is a signed RestaurantUser JWT obtained from
// POST /restaurant/authenticate. It is stored under its OWN localStorage key
// (`hw_staff_profile`) so it never collides with the customer `user_profile`. Every
// staff-gated call sends `Authorization: Bearer <token>`; the backend derives the
// staff userid + restaurant from req.decoded — the client never sends an identity.

import axios from 'axios';
import config from '../../config.json';

export const API = (config && config.SERVER_URL) || 'http://localhost:5001/api';

const STAFF_KEY = 'hw_staff_profile';

// ---- session ----
export function getStaffSession() {
  try {
    const raw = localStorage.getItem(STAFF_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.token) return null;
    return parsed; // { token, profile }
  } catch (e) {
    return null;
  }
}

export function setStaffSession(session) {
  try {
    localStorage.setItem(STAFF_KEY, JSON.stringify(session));
  } catch (e) {
    /* ignore quota / private-mode failures */
  }
}

export function clearStaffSession() {
  try {
    localStorage.removeItem(STAFF_KEY);
  } catch (e) {
    /* ignore */
  }
}

export function authHeaders() {
  const s = getStaffSession();
  return s && s.token ? { Authorization: `Bearer ${s.token}` } : {};
}

// ---- error normalisation (fail-safe, always returns a human string) ----
export function describeError(error) {
  if (!error) return 'Something went wrong. Please try again.';
  const res = error.response;
  if (res) {
    if (res.status === 401) return 'Your staff session expired. Please sign in again.';
    if (res.status === 403) return 'Not authorised. Please sign in again.';
    const d = res.data;
    if (typeof d === 'string' && d.trim()) return d;
    if (d && typeof d === 'object') {
      if (typeof d.Message === 'string') return d.Message;
      if (typeof d.message === 'string') return d.message;
      if (typeof d.reason === 'string') return prettyReason(d.reason);
      if (Array.isArray(d.Message) && d.Message[0] && d.Message[0].msg) return d.Message[0].msg;
    }
    return `Request failed (${res.status}).`;
  }
  if (error.message === 'Network Error') {
    return 'Cannot reach the server. Check your connection and try again.';
  }
  return error.message || 'Something went wrong. Please try again.';
}

// Map known backend reason codes to friendlier copy.
function prettyReason(reason) {
  const map = {
    burn_token_required: 'No voucher code was provided.',
    invalid_burn_token: 'This voucher code is invalid.',
    burn_token_expired: 'This voucher has expired.',
    voucher_not_found: 'Voucher not found.',
    not_owner: 'This voucher belongs to a different customer.',
    expired: 'This voucher has expired.',
    not_redeemable: 'This voucher cannot be redeemed.',
    reward_product_not_found: 'The reward item is no longer available.',
    product_unavailable: 'The reward item is currently unavailable.',
    not_redeemable_at_location: 'This reward can only be redeemed at another location.',
    campaign_paused: 'This stamp campaign is currently paused.',
    counter_collection_disabled: 'This campaign only earns stamps from completed orders.',
    scan_cooldown: 'Please wait a moment before stamping this customer again.',
    no_reward_available: 'No reward is available for this voucher right now.',
    reward_cap_missing: 'This reward is misconfigured (no value cap). Contact the manager.',
  };
  if (map[reason]) return map[reason];
  // reasons like "not_redeemable:redeemed"
  const base = String(reason).split(':')[0];
  if (map[base]) return map[base];
  return String(reason).replace(/_/g, ' ');
}

// ---- staff login ----
// POST {API}/restaurant/authenticate {email,password} -> {Message, token, profile}
export async function staffLogin(email, password) {
  const res = await axios.post(
    `${API}/restaurant/authenticate`,
    { email, password },
    { headers: { 'content-type': 'application/json' } },
  );
  const data = res.data || {};
  if (!data.token) {
    const msg = (data && (data.Message || data.message)) || 'Invalid credentials';
    throw new Error(typeof msg === 'string' ? msg : 'Invalid credentials');
  }
  return { token: data.token, profile: data.profile || {} };
}

// ---- campaigns for the staff's venue ----
// There is no dedicated staff-campaign-list endpoint; the unauthenticated
// GET /stamp/mobile/fetch returns all ACTIVE campaigns with a populated
// Restaurant. We filter to the staff's restaurant (incl. OtherRestaurants /
// cross-location campaigns) client-side. Identity for the staff actions still
// comes from the signed token (A4); this read is only to populate the picker.
export async function fetchVenueCampaigns(restaurantId, otherRestaurantIds) {
  const res = await axios.get(`${API}/stamp/mobile/fetch`);
  const all = Array.isArray(res.data) ? res.data : [];
  const venueSet = new Set(
    [restaurantId, ...(otherRestaurantIds || [])].filter(Boolean).map(String),
  );
  // No resolvable venue id → return an empty picker rather than every venue's
  // campaigns (a malformed staff profile must not be able to show a QR / grant
  // stamps for another venue's campaign). The server still enforces venue
  // scoping (A4) on every action; this is the client-side guard.
  if (venueSet.size === 0) return [];
  return all.filter((c) => {
    const own = c && c.Restaurant ? String(c.Restaurant._id || c.Restaurant) : null;
    if (own && venueSet.has(own)) return true;
    const others = Array.isArray(c && c.OtherRestaurants) ? c.OtherRestaurants : [];
    return others.some((o) => venueSet.has(String(o && o._id ? o._id : o)));
  });
}

export function campaignTitle(c) {
  if (!c) return 'Campaign';
  return (
    c.Title ||
    c.Name ||
    (c.RewardId && c.RewardId.Name && `Reward: ${c.RewardId.Name}`) ||
    `Card ${String(c._id || '').slice(-4)}`
  );
}
